import { environment } from 'environments/environment';
import { BehaviorSubject } from 'rxjs';
import { HubConnection, HubConnectionBuilder, HubConnectionState, HttpTransportType } from '@microsoft/signalr';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ConnectionResolver {
  public conversationHub!: HubConnection;
  public closed = new BehaviorSubject<boolean>(false);
  // false = connected/ok, true = disconnected/error — starts as false (no hub yet, not in error)
  public conversationHubStatus$ = new BehaviorSubject<boolean>(false);

  public async startconnection(authToken: string): Promise<void> {
    // If a connection is already active, do not recreate it
    if (
      this.conversationHub &&
      (this.conversationHub.state === HubConnectionState.Connected ||
        this.conversationHub.state === HubConnectionState.Connecting ||
        this.conversationHub.state === HubConnectionState.Reconnecting)
    ) {
      return;
    }

    // Stop and clean up any existing stale connection first
    if (this.conversationHub) {
      this.conversationHub.onclose(() => {});   // detach old handler to avoid ghost reconnects
      this.conversationHub.onreconnecting(() => {});
      this.conversationHub.onreconnected(() => {});
      try {
        await this.conversationHub.stop();
      } catch (_) {}
    }

    this.closed.next(false);

    this.conversationHub = new HubConnectionBuilder()
      .withUrl(`${environment.apiUrl}/hubs/conversations`, {
        accessTokenFactory: () => authToken,
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets
      })
      .withAutomaticReconnect()
      .build();

    this.conversationHub.onreconnecting(async error => {
      console.warn('SignalR is reconnecting due to error:', error);
      this.conversationHubStatus$.next(true);
    });

    this.conversationHub.onreconnected(async () => {
      console.log('SignalR reconnected.');
      this.conversationHubStatus$.next(false);
    });

    this.conversationHub.onclose(async () => {
      if (!this.closed.value) {
        console.warn('WebSocket disconnected, trying to reconnect...');
        this.conversationHubStatus$.next(true);
        await this.reconnectWithDelay();
      }
    });

    await this.startHub();
  }

  private async startHub(): Promise<void> {
    try {
      await this.conversationHub.start();
      this.conversationHubStatus$.next(false);
    } catch (err) {
      console.error('Error while trying to connect to WS:', err);
      this.conversationHubStatus$.next(true);
    }
  }

  private async reconnectWithDelay(): Promise<void> {
    let retries = 0;
    const maxRetries = 5;
    while (retries < maxRetries && !this.closed.value) {
      try {
        console.log(`Reconnection try: #${retries + 1}...`);
        await this.startHub();
        console.log('Reconnected!');
        return;
      } catch (err) {
        console.error('Failed to reconnect, new try in ', 2 ** retries * 1000, 'ms');
        retries++;
        await new Promise(res => setTimeout(res, 2 ** retries * 1000));
      }
    }
    console.error('Impossible to reconnect after ', maxRetries, ' tries.');
  }

  public sendMessage(method: string, data: any): void {
    if (this.conversationHub && this.conversationHub.state === HubConnectionState.Connected) {
      this.conversationHub.send(method, data).catch(err => console.error('Error while sending message :', err));
    } else {
      console.warn('Impossible to send message WS disconnected.');
    }
  }

  public closeHub(): void {
    if (this.conversationHub) {
      this.closed.next(true);
      this.conversationHub.stop().then(() => {
        this.conversationHubStatus$.next(true);
      });
    }
  }
}
